import React, { useState, useCallback, useContext } from "react";
import axios from "axios";
import { AccountContext } from "../../context/AccountContext";

const STATUS_META = {
  reconciled:  { label: "Reconciled",  bg: "#d4edda", color: "#155724" },
  matched:     { label: "Matched",     bg: "#d4edda", color: "#155724" },
  variance:    { label: "Variance",    bg: "#fff3cd", color: "#856404" },
  broker_only: { label: "Broker Only", bg: "#fff3cd", color: "#856404" },
  system_only: { label: "System Only", bg: "#f8d7da", color: "#721c24" },
  api_error:   { label: "API Error",   bg: "#f8d7da", color: "#721c24" },
};

const fmtPnl = (v) => {
  if (v == null) return <span style={{ color: "#aaa" }}>—</span>;
  const color = v >= 0 ? "#28a745" : "#dc3545";
  return <span style={{ color, fontWeight: 600 }}>{v >= 0 ? "+" : "−"}₹{Math.abs(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
};

const fmtPrice = (v) => v == null ? "—" : `₹${Number(v).toFixed(2)}`;

const Badge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, bg: "#e9ecef", color: "#333" };
  return (
    <span style={{
      background: m.bg, color: m.color,
      padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
      whiteSpace: "nowrap",
    }}>{m.label}</span>
  );
};

export default function ReconciliationPage() {
  const { selectedAccount } = useContext(AccountContext);
  const todayStr = new Date().toISOString().slice(0, 10);

  const [fromDate, setFromDate]     = useState(todayStr);
  const [toDate, setToDate]         = useState(todayStr);
  const [rows, setRows]             = useState(null);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [patching, setPatching]     = useState(false);
  const [patchResult, setPatchResult] = useState(null);
  const [error, setError]           = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const username = selectedAccount && selectedAccount !== "All Accounts" ? selectedAccount : null;

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true); setError(null); setPatchResult(null);
    try {
      const r = await axios.get("/pnl/reconciliation", {
        params: { username, from_date: fromDate, to_date: toDate },
      });
      setRows(r.data.rows);
      setSummary(r.data.summary);
    } catch (e) {
      setError("Failed to load reconciliation data.");
    } finally {
      setLoading(false);
    }
  }, [username, fromDate, toDate]);

  const patch = useCallback(async () => {
    if (!username) return;
    setPatching(true); setError(null);
    try {
      const r = await axios.post("/pnl/reconciliation/patch", { username });
      setPatchResult(r.data.patched);
      // Reload today's rows after patch
      const r2 = await axios.get("/pnl/reconciliation", {
        params: { username, from_date: fromDate, to_date: toDate },
      });
      setRows(r2.data.rows);
      setSummary(r2.data.summary);
    } catch (e) {
      setError(e.response?.data?.error || "Patch failed.");
    } finally {
      setPatching(false);
    }
  }, [username, fromDate, toDate]);

  const filtered = rows
    ? (statusFilter === "all" ? rows : rows.filter(r => r.status === statusFilter))
    : [];

  const hasIssues = rows && rows.some(r => ["variance", "broker_only", "system_only"].includes(r.status));

  return (
    <div style={{ padding: "20px 20px 40px", background: "#f4f6f9", minHeight: "100vh" }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 }}>
          F&O P&L Reconciliation
        </h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          Compare broker-reported P&L against our records. Broker values are authoritative.
        </p>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>From</div>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>To</div>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }} />
        </div>
        <button onClick={load} disabled={loading || !username}
          style={{ padding: "7px 20px", background: "#007bff", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
          {loading ? "Loading…" : "Load"}
        </button>
        {rows && hasIssues && (
          <button onClick={patch} disabled={patching}
            style={{ padding: "7px 20px", background: "#fd7e14", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            {patching ? "Patching…" : "Reconcile Now"}
          </button>
        )}
        {rows && !hasIssues && (
          <div style={{ padding: "7px 14px", background: "#d4edda", color: "#155724", borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
            ✓ Fully reconciled
          </div>
        )}
      </div>

      {!username && (
        <div style={{ color: "#888", padding: 20 }}>Select a specific account to view reconciliation.</div>
      )}
      {error && <div style={{ color: "#dc3545", marginBottom: 12 }}>{error}</div>}

      {patchResult && (
        <div style={{ background: "#d4edda", color: "#155724", padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          Reconciled: {patchResult.inserted} inserted · {patchResult.updated} updated · {patchResult.deleted} deleted
        </div>
      )}

      {/* ── Summary tiles ── */}
      {summary && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          {[
            { label: "Broker P&L",   val: summary.total_broker_pnl },
            { label: "Our P&L",      val: summary.total_our_pnl },
            { label: "Accepted P&L", val: summary.total_accepted_pnl },
          ].map(t => (
            <div key={t.label} style={{ background: "#fff", borderRadius: 10, padding: "12px 20px", minWidth: 160, boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{t.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtPnl(t.val)}</div>
            </div>
          ))}
          {Object.entries(summary.status_counts || {}).map(([st, cnt]) => {
            const m = STATUS_META[st] || { label: st, bg: "#e9ecef", color: "#333" };
            return (
              <div key={st} style={{ background: m.bg, borderRadius: 10, padding: "12px 20px", minWidth: 120, cursor: "pointer" }}
                onClick={() => setStatusFilter(statusFilter === st ? "all" : st)}>
                <div style={{ fontSize: 11, color: m.color, fontWeight: 700 }}>{m.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: m.color }}>{cnt}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Status filter chips ── */}
      {rows && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {["all", ...Object.keys(summary?.status_counts || {})].map(st => (
            <button key={st} onClick={() => setStatusFilter(st)}
              style={{
                padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: "1px solid #ddd", cursor: "pointer",
                background: statusFilter === st ? "#007bff" : "#fff",
                color: statusFilter === st ? "#fff" : "#555",
              }}>
              {st === "all" ? `All (${rows.length})` : `${STATUS_META[st]?.label || st} (${summary.status_counts[st]})`}
            </button>
          ))}
        </div>
      )}

      {/* ── Table ── */}
      {filtered.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,.06)", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                {["Date", "Symbol", "Strike", "Expiry", "Lots", "Sold At", "Bought At",
                  "Broker P&L", "Our P&L", "Variance", "Accepted P&L", "Status"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#444", fontSize: 12, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f0f0f0", background: i % 2 === 0 ? "#fff" : "#fcfcfc" }}>
                  <td style={td}>{r.exit_date}</td>
                  <td style={td}>
                    <span style={{ fontWeight: 600 }}>{r.symbol}</span>{" "}
                    <span style={{ color: r.optiontype === "CE" ? "#0d6efd" : "#e83e8c", fontWeight: 600, fontSize: 11 }}>{r.optiontype}</span>
                  </td>
                  <td style={td}>₹{r.strike.toLocaleString("en-IN")}</td>
                  <td style={td}>{r.expiry ? r.expiry.slice(0, 10) : "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}>{r.lots ?? "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}>{fmtPrice(r.sold_at)}</td>
                  <td style={{ ...td, textAlign: "right" }}>{fmtPrice(r.bought_at)}</td>
                  <td style={{ ...td, textAlign: "right" }}>{fmtPnl(r.broker_pnl)}</td>
                  <td style={{ ...td, textAlign: "right" }}>{fmtPnl(r.our_pnl)}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {r.variance == null ? <span style={{ color: "#aaa" }}>—</span>
                      : r.variance === 0 ? <span style={{ color: "#28a745" }}>₹0</span>
                      : fmtPnl(r.variance)}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>{fmtPnl(r.accepted_pnl)}</td>
                  <td style={td}><Badge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows && filtered.length === 0 && (
        <div style={{ color: "#888", padding: 30, textAlign: "center" }}>
          No entries for selected filter.
        </div>
      )}
    </div>
  );
}

const td = { padding: "9px 12px", whiteSpace: "nowrap" };
